import React, { useEffect, useRef, useCallback, useState } from "react";
import {
  Mail,
  MessageSquare,
  Check,
  User,
  ArrowLeft,
  Bell,
  Car as CarIcon,
  MapPin,
  Clock,
  Loader2,
  Info,
} from "lucide-react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  markInquiryAsRead,
  markAllInquiriesAsRead,
  subscribeToNotifications,
  subscribeToUnreadCount,
} from "@/store/partInquiry/partInquiryThunk";
import {
  resetNotifications,
  setLoading,
} from "@/store/partInquiry/partInquirySlice";
import PartChatDialog from "@/components/PartChatDialog";
import { fetchMessages, updateIsRead } from "../store/partChat/partChatActions";
import { toast } from "../components/ui/use-toast";
import { getEffectivePlan } from "../utils/subscriptionUtils";

const INITIAL_DISPLAY_COUNT = 10;
const LOAD_MORE_COUNT = 10;
const LOAD_MORE_DELAY = 800;

const Notifications = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const loaderRef = useRef(null);
  const unsubscribeNotificationsRef = useRef(null);
  const unsubscribeUnreadCountRef = useRef(null);

  const { user, currentSubscription, isAuthenticated } = useSelector(
    (state) => state.auth,
  );
  const userId = user?.uid;

  const { notifications, isLoading, unreadCount, isCountLoading, error } =
    useSelector((state) => state.inquries);

  const {
    messages: chatMessages,
    isMessagesLoading,
    isSenderLoading,
  } = useSelector((state) => state.partChat);

  const [selectedConversation, setSelectedConversation] = useState(null);
  const [markingReadId, setMarkingReadId] = useState(null);
  const [isMarkingAllRead, setIsMarkingAllRead] = useState(false);

  const [displayCount, setDisplayCount] = useState(INITIAL_DISPLAY_COUNT);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const currentPlan = getEffectivePlan(currentSubscription);

  const flattenedNotifications = notifications.flatMap((notification) =>
    notification.allInquiries.map((inquiry) => ({
      ...inquiry,
      buyerDetail: notification.buyerDetail,
      isReadByCurrentUser: inquiry.isRead?.includes(userId) || false,
    })),
  );

  const sortedNotifications = [...flattenedNotifications].sort(
    (a, b) => new Date(b.submittedAt) - new Date(a.submittedAt),
  );

  const displayedNotifications = sortedNotifications.slice(0, displayCount);
  const hasMore = displayCount < sortedNotifications.length;

  useEffect(() => {
    if (userId) {
      dispatch(setLoading(true));
      dispatch(resetNotifications());

      unsubscribeNotificationsRef.current = subscribeToNotifications(
        dispatch,
        userId,
      );

      unsubscribeUnreadCountRef.current = subscribeToUnreadCount(
        userId,
        dispatch,
      );
    }

    return () => {
      if (unsubscribeNotificationsRef.current) {
        unsubscribeNotificationsRef.current();
      }
      if (unsubscribeUnreadCountRef.current) {
        unsubscribeUnreadCountRef.current();
      }
    };
  }, [userId, dispatch]);

  useEffect(() => {
    if (!hasMore || isLoadingMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting) {
          handleLoadMore();
        }
      },
      { root: null, rootMargin: "100px", threshold: 0.1 },
    );

    if (loaderRef.current) {
      observer.observe(loaderRef.current);
    }

    return () => {
      if (loaderRef.current) {
        observer.unobserve(loaderRef.current);
      }
    };
  }, [hasMore, isLoadingMore, displayCount]);

  const handleLoadMore = useCallback(() => {
    if (isLoadingMore || !hasMore) return;

    setIsLoadingMore(true);

    setTimeout(() => {
      setDisplayCount((prev) => prev + LOAD_MORE_COUNT);
      setIsLoadingMore(false);
    }, LOAD_MORE_DELAY);
  }, [isLoadingMore, hasMore]);

  const handleMarkAsRead = useCallback(
    async (buyerId, inquiryId) => {
      if (userId && !markingReadId) {
        const uniqueId = `${buyerId}-${inquiryId}`;
        setMarkingReadId(uniqueId);

        try {
          await dispatch(
            markInquiryAsRead({ buyerId, inquiryId, userId }),
          ).unwrap();
        } catch (error) {
          console.error("Error marking as read:", error);
        } finally {
          setMarkingReadId(null);
        }
      }
    },
    [dispatch, userId, markingReadId],
  );

  const handleChatClick = async (notification) => {
    if (!userId) return;

    try {
      const buyerData = notification.buyerDetail;

      const conversation = {
        partId: notification?.partName?.toLowerCase().replace(/\s+/g, "-"),
        partName: notification.partName || "General Inquiry",
        partInfo: {
          id: notification?.partName?.toLowerCase().replace(/\s+/g, "-"),
          name: notification.partName || "General Inquiry",
          vehicleMake: notification.vehicleMake,
          vehicleModel: notification.vehicleModel,
          vehicleYear: notification.vehicleYear,
          postalCode: notification.postalCode,
        },
        otherUserDetail: {
          uid: buyerData.buyerId,
          id: buyerData.buyerId,
          name: buyerData.buyerName,
          displayName: buyerData.buyerName,
          email: buyerData.buyerEmail,
          photoURL: buyerData.buyerPhoto || null,
        },
      };

      dispatch(
        fetchMessages({
          buyerId: buyerData.buyerId,
          sellerId: userId,
          partId: notification?.partName?.toLowerCase().replace(/\s+/g, "-"),
          currentUserId: userId,
        }),
      );

      setTimeout(() => {
        dispatch(
          updateIsRead({
            currentUserId: userId,
            receiverId: buyerData.buyerId,
            partId: notification?.partName?.toLowerCase().replace(/\s+/g, "-"),
          }),
        );
      }, 1000);

      setSelectedConversation(conversation);
    } catch (error) {
      console.error("Error opening chat:", error);
    }
  };

  const MessageWithTooltip = ({ message }) => {
    const [isOverflowing, setIsOverflowing] = useState(false);
    const textRef = useRef(null);

    useEffect(() => {
      if (textRef.current) {
        const el = textRef.current;
        setIsOverflowing(el.scrollHeight > el.clientHeight + 2);
      }
    }, [message]);

    return (
      <div className="relative group mt-2">
        <p
          ref={textRef}
          className="text-sm text-muted-foreground line-clamp-1 md:line-clamp-2 cursor-pointer"
        >
          {message}
        </p>

        {isOverflowing && (
          <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block bg-card border border-border text-xs sm:text-sm text-foreground rounded-md shadow-lg p-2 max-w-xs whitespace-pre-wrap z-10 transition-all duration-200 opacity-0 group-hover:opacity-100 transform group-hover:-translate-y-1">
            {message}
          </div>
        )}
      </div>
    );
  };

  const handleMarkAllAsRead = useCallback(async () => {
    if (userId && unreadCount > 0 && !markingReadId) {
      setIsMarkingAllRead(true);

      try {
        await dispatch(markAllInquiriesAsRead({ userId })).unwrap();
      } catch (error) {
        console.error("Error marking all as read:", error);
      } finally {
        setIsMarkingAllRead(false);
      }
    }
  }, [dispatch, userId, unreadCount, markingReadId]);

  const formatDate = (date) => {
    if (!date) return "N/A";

    try {
      const d = date instanceof Date ? date : new Date(date);
      return d.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch {
      return "N/A";
    }
  };

  const Spinner = () => (
    <div className="flex justify-center items-center py-3">
      <Loader2 className="h-6 w-6 text-primary animate-spin" />
    </div>
  );

  useEffect(() => {
    if (!user || !isAuthenticated) {
      navigate("/");
      return;
    }

    if (user?.role === "seller" && currentPlan?.toLowerCase() !== "premium") {
      toast({
        title: "Access Denied",
        description: "You must buy the Premium Tier to see Notifications.",
        variant: "destructive",
      });
      navigate("/dashboard");
      return;
    }
  }, [user, isAuthenticated, navigate, currentPlan]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-background via-muted/10 to-background p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col sm:flex-row justify-between sm:items-center mb-10 w-full">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-full bg-primary/10">
              <Bell className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-primary via-purple-500 to-accent tracking-tight">
                Notifications
              </h1>
              {!isLoading && !isCountLoading && (
                <p className="text-base text-muted-foreground mt-2">
                  {unreadCount > 0
                    ? `You have ${unreadCount} unread ${
                        unreadCount === 1 ? "notification" : "notifications"
                      }`
                    : "All caught up — no unread notifications 🎉"}
                </p>
              )}
            </div>
          </div>

          <div className="w-full sm:w-auto mt-4 sm:mt-0 flex justify-end">
            <Button variant="outline" onClick={() => navigate("/dashboard")}>
              <ArrowLeft className="mr-2 h-4 w-4" /> Back to Dashboard
            </Button>
          </div>
        </div>

        {unreadCount > 0 && !isCountLoading && (
          <div className="flex justify-end mb-6">
            <Button
              size="sm"
              variant="outline"
              onClick={handleMarkAllAsRead}
              disabled={isMarkingAllRead || markingReadId !== null}
              className="transition-all font-medium"
            >
              {isMarkingAllRead ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />{" "}
                  Processing...
                </>
              ) : (
                <>
                  <Check className="mr-2 h-4 w-4" /> Mark All as Read
                </>
              )}
            </Button>
          </div>
        )}

        {error && (
          <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive">
            <p className="text-sm">{error}</p>
          </div>
        )}

        {/* Notifications List */}
        <div className="space-y-4">
          {isLoading && displayedNotifications.length === 0 ? (
            <div className="container mx-auto px-4 text-center py-10 flex flex-col justify-center items-center bg-gradient-to-br from-background to-secondary/5">
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, type: "spring" }}
                className="p-8 rounded-xl bg-card shadow-2xl"
              >
                <svg
                  className="animate-spin h-12 w-12 text-primary mx-auto mb-6"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                <h2 className="text-xl font-semibold text-foreground mb-2">
                  Loading Notifications
                </h2>
                <p className="text-muted-foreground">
                  Please wait while we fetch your notifications...
                </p>
              </motion.div>
            </div>
          ) : displayedNotifications.length === 0 && !isLoading ? (
            <EmptyState />
          ) : (
            <>
              {displayedNotifications.map((notification, index) => {
                const uniqueId = `${notification.buyerDetail.buyerId}-${notification.inquiryId}`;
                const isCurrentlyMarking = markingReadId === uniqueId;
                const isAnyMarking = markingReadId !== null || isMarkingAllRead;

                return (
                  <motion.div
                    key={`${notification.buyerDetail.buyerId}-${notification.inquiryId}-${index}`}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.02 }}
                    whileHover={{
                      scale: 1.01,
                      boxShadow: "0 6px 18px rgba(0,0,0,0.06)",
                    }}
                    className={`flex flex-col sm:flex-row sm:items-center sm:justify-between rounded-xl border px-5 py-4 transition-all duration-200 ${
                      !notification.isReadByCurrentUser
                        ? "border-primary/40 bg-primary/10"
                        : "border-border bg-background"
                    } ${
                      isAnyMarking && !isCurrentlyMarking ? "opacity-60" : ""
                    }`}
                  >
                    {/* LEFT SIDE */}
                    <div className="flex items-start gap-4 flex-1">
                      <div
                        className={`p-3 rounded-full shrink-0 ${
                          !notification.isReadByCurrentUser
                            ? "bg-primary/10"
                            : "bg-muted"
                        }`}
                      >
                        <User
                          className={`h-5 w-5 ${
                            !notification.isReadByCurrentUser
                              ? "text-primary"
                              : "text-muted-foreground"
                          }`}
                        />
                      </div>

                      <div className="flex-1">
                        <p className="text-base text-foreground font-semibold leading-snug">
                          <span className="text-primary">
                            {notification.buyerDetail.buyerName}
                          </span>{" "}
                          <span className="font-normal text-muted-foreground">
                            sent an inquiry for
                          </span>{" "}
                          <span className="text-primary font-semibold">
                            {notification.partName || "Unknown Part"}
                          </span>
                        </p>

                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <CarIcon className="h-4 w-4 text-muted-foreground" />
                            {notification.vehicleMake}{" "}
                            {notification.vehicleModel} (
                            {notification.vehicleYear})
                          </span>
                          <span className="hidden sm:inline">•</span>
                          <span className="flex items-center gap-1">
                            <MapPin className="h-4 w-4 text-muted-foreground" />
                            {notification.postalCode}
                          </span>
                          <span className="hidden sm:inline">•</span>
                          <span className="flex items-center gap-1">
                            <Clock className="h-4 w-4 text-muted-foreground" />
                            {formatDate(notification.submittedAt)}
                          </span>
                        </div>

                        {notification.message && (
                          <MessageWithTooltip message={notification.message} />
                        )}
                      </div>
                    </div>

                    {/* RIGHT SIDE BUTTONS */}
                    <div className="flex flex-col sm:flex-row items-end sm:items-center gap-3 mt-4 sm:mt-0 sm:ml-4 w-full sm:w-auto justify-end">
                      <div className="flex flex-wrap gap-2 justify-end w-full sm:w-auto">
                        <Button
                          size="sm"
                          variant="outline"
                          className="w-auto flex items-center justify-center gap-1"
                          onClick={() => handleChatClick(notification)}
                          disabled={isAnyMarking}
                        >
                          <MessageSquare className="h-4 w-4" /> Chat
                        </Button>
                        {!notification.isReadByCurrentUser && (
                          <Button
                            size="sm"
                            className="w-auto flex items-center justify-center gap-1 relative"
                            onClick={() =>
                              handleMarkAsRead(
                                notification.buyerDetail.buyerId,
                                notification.inquiryId,
                              )
                            }
                            disabled={isAnyMarking}
                          >
                            {isCurrentlyMarking ? (
                              <>
                                <Loader2 className="h-4 w-4 animate-spin" />{" "}
                              </>
                            ) : (
                              <>
                                <Check className="h-4 w-4" /> Read
                              </>
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </>
          )}

          {/* Load More Indicator */}
          <div
            ref={loaderRef}
            className="h-10 flex justify-center items-center"
          >
            {isLoadingMore && <Spinner />}
          </div>

          {/* End of List Indicator */}
          {!hasMore && displayedNotifications.length > 0 && !isLoading && (
            <div className="flex items-center justify-center">
              <div className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border/60 bg-muted/30 text-muted-foreground shadow-sm">
                <Info className="w-5 h-5" />
                <span className="text-sm font-medium">
                  No more notifications
                </span>
              </div>
            </div>
          )}

          {/* Show count information */}
          {sortedNotifications.length > INITIAL_DISPLAY_COUNT &&
            displayedNotifications.length > 0 && (
              <div className="flex items-center justify-center mt-4">
                <p className="text-sm text-muted-foreground">
                  Showing {displayedNotifications.length} of{" "}
                  {sortedNotifications.length} notifications
                </p>
              </div>
            )}
        </div>
      </div>

      {selectedConversation && (
        <PartChatDialog
          messages={chatMessages}
          conversation={selectedConversation}
          isOpen={!!selectedConversation}
          isMessagesLoading={isMessagesLoading}
          isSenderLoading={isSenderLoading}
          onOpenChange={(open) => {
            if (!open) setSelectedConversation(null);
          }}
        >
          <></>
        </PartChatDialog>
      )}
    </div>
  );
};

const EmptyState = () => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    className="bg-card rounded-xl border border-border/50 shadow-sm p-12 text-center"
  >
    <Mail className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
    <h3 className="text-xl font-semibold mb-2">No notifications</h3>
    <p className="text-sm text-muted-foreground">
      When buyers send inquiries, they'll appear here.
    </p>
  </motion.div>
);

export default Notifications;
